import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  HStack,
  Badge,
  Text,
  useToast,
  IconButton,
} from '@chakra-ui/react';
import { FiEye, FiFileText } from 'react-icons/fi';
import DataTable, { Column } from '@/components/common/DataTable';
import apiClient , { Payment } from '@/services/letsparkyClient';
import { useAuthStore } from '@/stores/authStore';
import { useTranslation } from 'react-i18next';

const Payments: React.FC = () => {
  const toast = useToast();
  const { user, token } = useAuthStore();
  const isSystem = user?.role === 'SYSTEM';
  
  const [payments, setPayments] = useState<Payment[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sort_by, setSortBy] = useState<string>('');
  const [sort_direction, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const { t } = useTranslation();

  const fetchPayments = async () => {
    setIsLoading(true);
    try {
      if (!token) throw new Error('Authentication token not available');
      apiClient.client.setToken(token);
      
      // Build parameters object with search functionality
      const params: any = {
        tenant_id: isSystem ? undefined : user?.tenantId
      };
      
      // Add search parameter if search query exists
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      // Add sorting parameters
      if (sort_by) {
        params.sort_by = sort_by;
        params.sort_direction = sort_direction;
      }
      
      const response = await apiClient.payments.listPaginated({
        page: currentPage, 
        limit: pageSize
      }, params);
      setPayments(response.data);
      setTotalItems(response.pagination.total);
    } catch (error) {
      toast({
        title: 'Error fetching payments',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchPayments();
    }
  }, [currentPage, pageSize, searchQuery, sort_by, sort_direction, user?.tenantId, token]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleSort = (newSortBy: string, newSortDirection: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortDirection(newSortDirection);
    setCurrentPage(1); // Reset to first page when sorting
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'green';
      case 'PENDING':
        return 'yellow';
      case 'FAILED':
        return 'red';
      default:
        return 'gray';
    }
  };

  const getMethodIcon = (method: string) => {
    switch (method) {
      case 'CARD':
        return '💳';
      case 'CASH':
        return '💵';
      case 'MOBILE':
        return '📱';
      default:
        return '❓';
    }
  };

  const columns: Column<Payment>[] = [
    {
      header: 'ID',
      accessor: 'id',
      sortable: true,
      sortKey: 'id',
    },
    {
      header: 'Parking ID',
      accessor: 'parking_id',
      sortable: true,
      sortKey: 'parking_id',
    },
    {
      header: 'Amount',
      accessor: (payment) => `$${payment.amount.toFixed(2)}`,
      isNumeric: true,
      sortable: true,
      sortKey: 'amount',
    },
    {
      header: 'Method',
      accessor: (payment) => (
        <HStack>
          <Text>{getMethodIcon(payment.method)}</Text>
          <Text>{payment.method}</Text>
        </HStack>
      ),
      sortable: true,
      sortKey: 'method',
    },
    {
      header: 'Status',
      accessor: (payment) => (
        <Badge colorScheme={getStatusColor(payment.status)}>
          {payment.status}
        </Badge>
      ),
      sortable: true,
      sortKey: 'status',
    },
    ...(isSystem ? [{
      header: 'Tenant',
      accessor: 'tenantId' as keyof Payment,
      sortable: true,
      sortKey: 'tenant_id',
    }] : []),
    {
      header: 'Transaction ID',
      accessor: (payment) => payment.transaction_id || <Text color="gray.500">-</Text>,
      sortable: true,
      sortKey: 'transaction_id',
    },
    {
      header: 'Date',
      accessor: (payment) => new Date(payment.created_at).toLocaleString(),
      sortable: true,
      sortKey: 'created_at',
    },
  ];

  const actionButtons = (payment: Payment) => (
    <>
      <IconButton
        aria-label="View details"
        icon={<FiEye />}
        size="sm"
        colorScheme="blue"
        variant="ghost"
        onClick={() => console.log('View payment details', payment.id)}
      />
      <IconButton
        aria-label="Generate receipt"
        icon={<FiFileText />}
        size="sm"
        colorScheme="teal"
        variant="ghost"
        onClick={() => console.log('Generate receipt', payment.id)}
        isDisabled={payment.status !== 'CAPTURED'}
      />
    </>
  );

  return (
    <Box>
      <Heading size="lg" mb={6}>{t('payments.title')}</Heading>

      <DataTable
        columns={columns}
        data={payments}
        keyField="id"
        isLoading={isLoading}
        totalItems={totalItems}
        currentPage={currentPage}
        pageSize={pageSize}
        sort_by={sort_by}
        sort_direction={sort_direction}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onSearch={handleSearch}
        onSort={handleSort}
        actionButtons={actionButtons}
      />
    </Box>
  );
};

export default Payments; 